import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_MIN_WIDTH = 72;

const loadStoredWidths = (storageKey) => {
  if (!storageKey || typeof window === "undefined") {
    return {};
  }
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(storageKey) || "{}");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      if (parsed.widths && typeof parsed.widths === "object") {
        return parsed.widths;
      }
      return parsed;
    }
  } catch {
    return {};
  }
  return {};
};

const persistWidths = (storageKey, widths) => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }
  window.sessionStorage.setItem(storageKey, JSON.stringify({ widths }));
};

const buildWidthMap = (columnKeys, storedWidths, resolveDefault, minWidth) => {
  const next = {};
  columnKeys.forEach((key) => {
    const stored = storedWidths[key];
    const fallback = resolveDefault(key);
    const width = stored != null ? Number(stored) : fallback;
    next[key] = Math.max(minWidth, Number.isFinite(width) ? width : fallback);
  });
  return next;
};

const widthMapsEqual = (left, right) => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) {
    return false;
  }
  return leftKeys.every((key) => left[key] === right[key]);
};

export const useResizableColumns = (columnKeys, options = {}) => {
  const {
    defaultWidth = 140,
    defaultWidths = {},
    minWidth = DEFAULT_MIN_WIDTH,
    storageKey,
    resizableKeys,
  } = options;

  const columnKeysKey = columnKeys.join("\u0001");
  const defaultWidthRef = useRef(defaultWidth);
  const defaultWidthsRef = useRef(defaultWidths);
  const minWidthRef = useRef(minWidth);
  defaultWidthRef.current = defaultWidth;
  defaultWidthsRef.current = defaultWidths;
  minWidthRef.current = minWidth;

  const resolveDefault = useCallback((columnKey) => {
    return defaultWidthsRef.current[columnKey] ?? defaultWidthRef.current;
  }, []);

  const resizableSet = useMemo(() => {
    if (Array.isArray(resizableKeys) && resizableKeys.length > 0) {
      return new Set(resizableKeys);
    }
    return new Set(columnKeys);
  }, [columnKeysKey, resizableKeys]);

  const [widths, setWidths] = useState(() => {
    const stored = loadStoredWidths(storageKey);
    return buildWidthMap(columnKeys, stored, resolveDefault, minWidthRef.current);
  });

  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  useEffect(() => {
    setWidths((prev) => {
      const stored = loadStoredWidths(storageKey);
      const next = buildWidthMap(
        columnKeys,
        { ...stored, ...prev },
        resolveDefault,
        minWidthRef.current,
      );
      return widthMapsEqual(prev, next) ? prev : next;
    });
  }, [columnKeysKey, resolveDefault, storageKey]);

  const getWidth = useCallback(
    (columnKey) => widths[columnKey] ?? resolveDefault(columnKey),
    [widths, resolveDefault],
  );

  const getCellStyle = useCallback(
    (columnKey) => {
      const width = getWidth(columnKey);
      return {
        width,
        minWidth: width,
        maxWidth: width,
        boxSizing: "border-box",
      };
    },
    [getWidth],
  );

  const tableMinWidth = useMemo(
    () => columnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [columnKeysKey, widths, resolveDefault],
  );

  const startResize = useCallback((event, columnKey) => {
    if (!resizableSet.has(columnKey)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const startX = event.clientX;
    const startWidth = widthsRef.current[columnKey] ?? resolveDefault(columnKey);
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMove = (moveEvent) => {
      const nextWidth = Math.max(minWidthRef.current, startWidth + (moveEvent.clientX - startX));
      setWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
    };

    const onUp = () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = "";
      persistWidths(storageKey, widthsRef.current);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [resolveDefault, resizableSet, storageKey]);

  return {
    getWidth,
    getCellStyle,
    startResize,
    tableMinWidth,
  };
};
